import { Combobox } from '@base-ui/react'
import { useEffect, useId, useMemo, useState } from 'react'

import type { FilterTypeaheadOption } from './types'

type FilterTypeaheadItem = {
  code: string
  description: string
}

type FilterTypeaheadProps = {
  label: string
  value: string
  onChange: (value: string) => void
  options: FilterTypeaheadOption[]
  placeholder?: string
  className?: string
}

const FilterTypeahead = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  className = 'flex-1',
}: FilterTypeaheadProps) => {
  const items = useMemo(
    () =>
      options.map(option => ({
        code: option.value,
        description: option.label || '',
      })),
    [options],
  )
  const [inputValue, setInputValue] = useState(value)
  const inputId = useId()

  const selectedItem = useMemo(
    () => items.find(item => item.code === value) ?? null,
    [items, value],
  )

  useEffect(() => {
    setInputValue(value)
  }, [value])

  const commitValue = () => {
    const normalizedValue = inputValue.trim().toLowerCase()

    if (!normalizedValue) {
      setInputValue('')

      if (value !== '') {
        onChange('')
      }

      return
    }

    const matchingItem = items.find(
      item => item.code.toLowerCase() === normalizedValue,
    )

    if (matchingItem) {
      setInputValue(matchingItem.code)

      if (matchingItem.code !== value) {
        onChange(matchingItem.code)
      }

      return
    }

    setInputValue('')

    if (value !== '') {
      onChange('')
    }
  }

  return (
    <div className={className}>
      <Combobox.Root
        items={items}
        value={selectedItem}
        inputValue={inputValue}
        openOnInputClick
        autoHighlight
        limit={50}
        itemToStringLabel={item => item.code}
        itemToStringValue={item => item.code}
        isItemEqualToValue={(item, selectedValue) =>
          item.code === selectedValue.code
        }
        filter={(item: FilterTypeaheadItem, query) => {
          const normalizedQuery = query.trim().toLowerCase()

          if (!normalizedQuery) {
            return true
          }

          return (
            item.code.toLowerCase().includes(normalizedQuery) ||
            item.description.toLowerCase().includes(normalizedQuery)
          )
        }}
        onInputValueChange={nextValue => {
          setInputValue(nextValue)

          if (nextValue === '' && value !== '') {
            onChange('')
          }
        }}
        onValueChange={nextValue => {
          if (!nextValue) {
            return
          }

          setInputValue(nextValue.code)

          if (nextValue.code !== value) {
            onChange(nextValue.code)
          }
        }}
      >
        <div className="form-control">
          <label className="label" htmlFor={inputId}>
            <span className="label-text">{label}</span>
          </label>
          <div className="relative">
            <Combobox.Input
              className="input input-bordered w-full"
              id={inputId}
              placeholder={placeholder}
              onBlur={event => {
                if (
                  event.relatedTarget instanceof Node &&
                  event.currentTarget.parentElement?.contains(
                    event.relatedTarget,
                  )
                ) {
                  return
                }

                commitValue()
              }}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  commitValue()
                }
              }}
            />
            <Combobox.Portal>
              <Combobox.Positioner sideOffset={4}>
                <Combobox.Popup className="z-30 w-(--anchor-width) max-h-72 overflow-y-auto rounded-box border border-base-300 bg-base-100 shadow-xl">
                  <Combobox.Empty className="w-full px-3 py-2 text-sm text-base-content/70">
                    No matches found
                  </Combobox.Empty>
                  <Combobox.List className="w-full p-1">
                    {(item: FilterTypeaheadItem) => (
                      <Combobox.Item
                        key={item.code}
                        value={item}
                        className="flex cursor-default flex-col items-start rounded-md px-3 py-2 text-left data-highlighted:bg-base-200 data-selected:bg-base-200/70"
                      >
                        <span className="font-mono uppercase">{item.code}</span>
                        {item.description ? (
                          <span className="text-xs text-base-content/70">
                            {item.description}
                          </span>
                        ) : null}
                      </Combobox.Item>
                    )}
                  </Combobox.List>
                </Combobox.Popup>
              </Combobox.Positioner>
            </Combobox.Portal>
          </div>
        </div>
      </Combobox.Root>
    </div>
  )
}

export type { FilterTypeaheadProps }
export { FilterTypeahead }
